import { DeviceEnrichment, FirestoreFilter } from '../types';

export interface PhotoEntry {
  /** The photo as it was uploaded. Always the original, never a derivative. */
  url: string;
  label: string;
  source: string;
  /**
   * A copy the server made that a browser can actually draw — a JPEG rendered
   * from a HEIC. Set from the record's newest preview when it has one, else
   * from its newest label read's converted copy; unset when neither exists.
   */
  previewUrl?: string;
  /** What that copy was made from, e.g. `heic`. */
  convertedFrom?: string;
  /** FS-1079 — the stored clockwise turn the photo is drawn with; unset when it is drawn as it is. */
  rotation?: number;
}

/**
 * Every photo URL on one record, in the order they were stored.
 *
 * `labelPhotoUrls` is the list: the app writes the first one and the console's
 * operator route appends to it, setting the singular `labelPhotoUrl` only when
 * there was nothing there at all. So the array comes first and the singular
 * fields are a fallback for records written before it existed — and a URL in
 * both is one photo, not two.
 */
function recordUrls(record: any): string[] {
  const urls: string[] = [];
  const push = (url: unknown) => {
    if (typeof url === 'string' && url && !urls.includes(url)) urls.push(url);
  };
  if (Array.isArray(record?.labelPhotoUrls)) record.labelPhotoUrls.forEach(push);
  push(record?.labelPhotoUrl);
  push(record?.photoUrl);
  return urls;
}

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value !== '';

/**
 * The newest of a record's entries for one photo. Entries arrive in whatever
 * order `arrayUnion` left them, so the array position is only the tiebreak:
 * an entry with a readable time beats one without, and between two that tie
 * the later one wins, because `arrayUnion` appends. Anything that is not an
 * object naming this photo with a drawable URL is skipped, never thrown on;
 * with `requireTime`, so is an entry whose time cannot be read.
 */
function newestFor(
  list: unknown,
  url: string,
  drawable: (entry: any) => unknown,
  time: (entry: any) => unknown,
  requireTime = false,
): any | null {
  if (!Array.isArray(list)) return null;
  let best: any = null;
  let bestMs = -Infinity;
  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object' || entry.url !== url || !nonEmpty(drawable(entry))) return;
    const read = firestoreMillis(time(entry));
    if (read === null && requireTime) return;
    const ms = read ?? -Infinity;
    if (best === null || ms >= bestMs) {
      best = entry;
      bestMs = ms;
    }
  });
  return best;
}

/**
 * The copy a browser can draw for one photo on one record (FS-780), by the
 * rule the equipment API's `preview_url` follows too, so the Photos tab and
 * the edit drawer always draw the same file:
 *   1. the newest `labelPhotoPreviews` entry for it — written on EVERY record
 *      the photo is attached to, including one it was routed to, which has no
 *      read of its own. An entry with no readable `at` is skipped, as the API
 *      skips it; an undated analysis below is kept but loses to a dated one;
 *   2. else the newest `labelPhotoAnalyses` entry for it with an `analyzedUrl`
 *      (by `analyzedAt` — this took the first one until FS-780);
 *   3. else nothing, and the upload itself is drawn.
 */
function derivativeFor(record: any, url: string): Pick<PhotoEntry, 'previewUrl' | 'convertedFrom'> {
  const preview = newestFor(record?.labelPhotoPreviews, url, (e) => e.previewUrl, (e) => e.at, true);
  if (preview) {
    return {
      previewUrl: preview.previewUrl,
      ...(nonEmpty(preview.from) ? { convertedFrom: preview.from } : {}),
    };
  }
  const analysis = newestFor(record?.labelPhotoAnalyses, url, (e) => e.analyzedUrl, (e) => e.analyzedAt);
  if (!analysis) return {};
  const from = analysis.derivative?.from;
  return { previewUrl: analysis.analyzedUrl, ...(nonEmpty(from) ? { convertedFrom: from } : {}) };
}

/** The turns a display applies (FS-1079). */
const TURNS = [0, 90, 180, 270];

/** One usable `labelPhotoRotations` entry. */
interface StoredTurn {
  url: string;
  degrees: number;
  source: 'operator' | 'automatic';
  at: unknown;
}

/** A stored entry as a turn a display can apply, or null for anything else. */
function storedTurnOf(entry: unknown): StoredTurn | null {
  if (entry === null || typeof entry !== 'object') return null;
  const url = 'url' in entry ? entry.url : undefined;
  const degrees = 'degrees' in entry ? entry.degrees : undefined;
  const source = 'source' in entry ? entry.source : undefined;
  if (typeof url !== 'string' || typeof degrees !== 'number' || !TURNS.includes(degrees)) return null;
  if (source !== 'operator' && source !== 'automatic') return null;
  return { url, degrees, source, at: 'at' in entry ? entry.at : undefined };
}

/** A download URL without its token: the same Storage object under any token is the same photo. */
const objectOf = (url: unknown): string | null =>
  typeof url === 'string' && url !== '' ? url.split(/[?#]/)[0] : null;

/**
 * FS-1079 — the stored turn for one photo on one record, by the rule the
 * equipment API's `rotation` follows too (`photo_rotation.rotationEntryOf`),
 * so the Photos tab and the edit drawer draw it the same way: among the
 * record's `labelPhotoRotations` entries for the photo (any download token)
 * with a valid turn, an operator's beats an automatic one, then the newest
 * `at`. Unset when there is none or it is 0, as `previewUrl` is unset when
 * nothing was converted.
 */
function rotationFor(
  record: { labelPhotoRotations?: unknown } | null | undefined,
  url: string,
): Pick<PhotoEntry, 'rotation'> {
  const list = record?.labelPhotoRotations;
  if (!Array.isArray(list)) return {};
  const photo = objectOf(url);
  let best: StoredTurn | null = null;
  for (const entry of list) {
    const turn = storedTurnOf(entry);
    if (!turn || objectOf(turn.url) !== photo) continue;
    const wins = best === null || (turn.source !== best.source
      ? turn.source === 'operator'
      : (firestoreMillis(turn.at) ?? -Infinity) > (firestoreMillis(best.at) ?? -Infinity));
    if (wins) best = turn;
  }
  return best && best.degrees !== 0 ? { rotation: best.degrees } : {};
}

type AddPhoto = (record: any, url: string, label: string, source: string) => void;

/**
 * A filter record's photos: its label photos, then its own gallery, which is
 * not a label photo and never has been.
 */
function addFilterPhotos(
  filter: any,
  add: AddPhoto,
  label: string,
  galleryLabel: (n: number) => string,
  source: string,
): void {
  recordUrls(filter).forEach((url) => add(filter, url, label, source));
  if (Array.isArray(filter?.photoUrls)) {
    filter.photoUrls.forEach((url: unknown, idx: number) => {
      if (typeof url === 'string' && url) add(filter, url, galleryLabel(idx + 1), source);
    });
  }
}

/**
 * The photos on the records the device points at now — HVAC systems, A/C
 * systems and the current filter — split into the equipment's and the
 * filter's. A URL is one photo across all of them, which is how the flat list
 * has always counted.
 */
function currentRecordPhotos(enrichment: DeviceEnrichment): {
  equipment: PhotoEntry[];
  filter: PhotoEntry[];
} {
  const equipment: PhotoEntry[] = [];
  const filterPhotos: PhotoEntry[] = [];
  const seen = new Set<string>();

  const into =
    (list: PhotoEntry[]): AddPhoto =>
    (record, url, label, source) => {
      if (seen.has(url)) return;
      seen.add(url);
      list.push({ url, label, source, ...derivativeFor(record, url), ...rotationFor(record, url) });
    };
  const addEquipment = into(equipment);

  enrichment.hvacSystems?.forEach((hvac: any, idx: number) => {
    const prefix = enrichment.hvacSystems.length > 1 ? `HVAC ${idx + 1}` : 'HVAC';
    const label = `${prefix}: ${hvac.name || 'Unnamed'}`;
    recordUrls(hvac).forEach((url) => addEquipment(hvac, url, label, 'HVAC System'));
  });

  enrichment.acSystems?.forEach((ac: any, idx: number) => {
    const prefix = enrichment.acSystems.length > 1 ? `AC ${idx + 1}` : 'AC';
    const label = `${prefix}: ${ac.name || 'Unnamed'}`;
    recordUrls(ac).forEach((url) => addEquipment(ac, url, label, 'AC System'));
  });

  const filter: any = enrichment.filter;
  if (filter) {
    const label = `Filter: ${filter.manufacturer || 'Unknown'} ${filter.rating || ''}`.trim();
    addFilterPhotos(filter, into(filterPhotos), label, (n) => `Filter photo ${n}`, 'Filter');
  }

  return { equipment, filter: filterPhotos };
}

/**
 * Collect all photo URLs from an enriched device record.
 * Looks at HVAC systems, A/C systems, and the CURRENT filter — label photo
 * arrays first, then the singular fields legacy records still use. Earlier
 * filter records are not included; `groupDevicePhotos` adds those.
 */
export function collectDevicePhotos(enrichment: DeviceEnrichment | undefined): PhotoEntry[] {
  if (!enrichment) return [];
  const { equipment, filter } = currentRecordPhotos(enrichment);
  return [...equipment, ...filter];
}

/** The photos on the records the device points at now (see collectDevicePhotos). */
export function countDevicePhotos(enrichment: DeviceEnrichment | undefined): number {
  return collectDevicePhotos(enrichment).length;
}

/**
 * A Firestore date as the calendar day it names, YYYY-MM-DD in UTC: the same
 * on every operator's machine, and exactly the day an operator typed, because
 * the console's filter-change route stores a typed date as UTC midnight.
 * Takes every shape an install date reaches the console in (a serialized
 * Timestamp, its public-field twin, an ISO string, epoch milliseconds);
 * anything it cannot read is null, never a guess.
 */
export function firestoreUtcDay(value: unknown): string | null {
  const ms = firestoreMillis(value);
  if (ms === null) return null;
  const date = new Date(ms);
  // Out of Date's range: toISOString would throw.
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

/**
 * An ISO 8601 date or date-time: what the readers on both sides accept as a
 * string. `Date.parse` alone also takes "2026" or "12" (V8 reads them as
 * years and dates), and an epoch number written as a string — none of which is
 * a timestamp anyone stored on purpose.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * A Firestore date as epoch milliseconds (see firestoreUtcDay); null for
 * anything else. Exactly these shapes, the same set firebase-api's reader
 * takes, so the drawer and the Photos tab rank photos the same way:
 *   - a Timestamp's serialized private fields `{_seconds, _nanoseconds}`;
 *   - its public fields `{seconds, nanoseconds}` (a live Timestamp object);
 *   - an ISO 8601 string;
 *   - epoch milliseconds as a number.
 */
export function firestoreMillis(value: unknown): number | null {
  let ms: number | null = null;
  if (typeof value === 'number') {
    ms = value;
  } else if (typeof value === 'string') {
    ms = ISO_DATE.test(value) ? Date.parse(value) : null;
  } else if (value && typeof value === 'object') {
    const t = value as {
      _seconds?: unknown;
      _nanoseconds?: unknown;
      seconds?: unknown;
      nanoseconds?: unknown;
    };
    const seconds =
      typeof t._seconds === 'number' ? t._seconds : typeof t.seconds === 'number' ? t.seconds : null;
    if (seconds !== null) {
      const nanos =
        typeof t._nanoseconds === 'number'
          ? t._nanoseconds
          : typeof t.nanoseconds === 'number'
            ? t.nanoseconds
            : 0;
      ms = seconds * 1000 + Math.floor(nanos / 1e6);
    }
  }
  if (ms === null || !Number.isFinite(ms)) return null;
  return ms;
}

/** "installed 2026-07-25", or that the record carries no install date. */
export function installedPhrase(day: string | null): string {
  return day ? `installed ${day}` : 'install date not recorded';
}

/** Brand and rating as the record has them, e.g. "BNX MERV 13"; '' when neither is known. */
export function filterIdentity(filter: Partial<FirestoreFilter> | null | undefined): string {
  if (!filter) return '';
  return [filter.manufacturer, filter.rating]
    .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''))
    .filter(Boolean)
    .join(' ');
}

/** The `source` every photo from an earlier filter record carries. */
export const PREVIOUS_FILTER_SOURCE = 'Previous filter';

/**
 * One earlier filter record's photos (FS-539), read exactly as the current
 * records are: label photos first, the converted copy where a read made one,
 * then the filter's own gallery. Deduplicated within this record only — a URL
 * that also sits on the current record (a photo carried forward) is still this
 * filter's photo, and dropping it would hide what the section exists to show.
 */
export function previousFilterPhotos(filter: FirestoreFilter): PhotoEntry[] {
  const photos: PhotoEntry[] = [];
  const seen = new Set<string>();
  const add: AddPhoto = (record, url, label, source) => {
    if (seen.has(url)) return;
    seen.add(url);
    photos.push({ url, label, source, ...derivativeFor(record, url), ...rotationFor(record, url) });
  };
  const when = installedPhrase(firestoreUtcDay(filter?.installDate));
  const identity = filterIdentity(filter) || 'Unknown';
  addFilterPhotos(
    filter,
    add,
    `Filter (${when}): ${identity}`,
    (n) => `Filter photo ${n} (${when})`,
    PREVIOUS_FILTER_SOURCE,
  );
  return photos;
}

/** One filter record as the Photos tab shows it. */
export interface FilterPhotoSection {
  /** The filter record's document id, when the payload carries one. */
  id: string | null;
  /** YYYY-MM-DD, or null when the record has no install date that can be read. */
  installDay: string | null;
  /** Brand and rating, e.g. "BNX MERV 13"; '' when neither is known. */
  identity: string;
  photos: PhotoEntry[];
}

export interface DevicePhotoGroups {
  /** HVAC and A/C photos. */
  equipment: PhotoEntry[];
  /** The filter the device points at now; null when it points at none. */
  currentFilter: FilterPhotoSection | null;
  /** Earlier filter records that have photos, in the order given: newest install first. */
  previousFilters: FilterPhotoSection[];
  /** Earlier filter records with no photos: counted, never drawn as empty sections. */
  previousWithoutPhotos: number;
  /** Every photo in every group. */
  total: number;
}

/**
 * The Photos tab's grouping (FS-539): the equipment, the current filter, and
 * each earlier filter on its own, so a filter change no longer takes the old
 * filter's photos out of view.
 *
 * `enrichment` is usually the batch map's entry and `previousFilters` comes
 * from the single-device read; they are fetched separately. A record the
 * batch entry calls current is never also drawn as an earlier one, which is
 * what the moment after a filter change looks like when one read has caught
 * up and the other has not.
 */
export function groupDevicePhotos(
  enrichment: DeviceEnrichment | undefined,
  previousFilters?: FirestoreFilter[] | null,
): DevicePhotoGroups {
  if (!enrichment) {
    return { equipment: [], currentFilter: null, previousFilters: [], previousWithoutPhotos: 0, total: 0 };
  }
  const { equipment, filter: currentPhotos } = currentRecordPhotos(enrichment);

  const current: any = enrichment.filter;
  const hasCurrent = Boolean(current && typeof current === 'object' && Object.keys(current).length > 0);
  const currentFilter: FilterPhotoSection | null = hasCurrent
    ? {
        id: typeof current.id === 'string' ? current.id : null,
        installDay: firestoreUtcDay(current.installDate),
        identity: filterIdentity(current),
        photos: currentPhotos,
      }
    : null;

  const currentIds = new Set(
    [enrichment.device?.filterId, current?.id].filter(
      (v): v is string => typeof v === 'string' && v !== '',
    ),
  );
  const sections: FilterPhotoSection[] = (Array.isArray(previousFilters) ? previousFilters : [])
    .filter((f) => Boolean(f) && typeof f === 'object' && !currentIds.has(f.id))
    .map((f) => ({
      id: typeof f.id === 'string' ? f.id : null,
      installDay: firestoreUtcDay(f.installDate),
      identity: filterIdentity(f),
      photos: previousFilterPhotos(f),
    }));
  const withPhotos = sections.filter((s) => s.photos.length > 0);

  return {
    equipment,
    currentFilter,
    previousFilters: withPhotos,
    previousWithoutPhotos: sections.length - withPhotos.length,
    total:
      equipment.length +
      currentPhotos.length +
      withPhotos.reduce((sum, s) => sum + s.photos.length, 0),
  };
}
